/**
 * Resource Optimization Utilities
 *
 * Utilities for optimizing images, fonts, and other resources
 */

import React from 'react';

/**
 * Lazy load images with intersection observer
 */
export const lazyLoadImages = () => {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            observer.unobserve(img);
          }
        }
      });
    });

    // Observe all images with data-src attribute
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
};

/**
 * Preload critical resources
 */
export const preloadResources = (resources: string[]) => {
  resources.forEach(resource => {
    if (resource.endsWith('.js')) {
      const link = document.createElement('link');
      link.rel = 'modulepreload';
      link.href = resource;
      link.as = 'script';
      document.head.appendChild(link);
    } else if (resource.endsWith('.css')) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource;
      link.as = 'style';
      document.head.appendChild(link);
    } else if (resource.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource;
      link.as = 'image';
      document.head.appendChild(link);
    }
  });
};

/**
 * Get optimized image URL based on device pixel ratio
 */
export const getOptimizedImageUrl = (baseUrl: string): string => {
  const dpr = window.devicePixelRatio || 1;

  // If DPR is 1 or less, use original image
  if (dpr <= 1) {
    return baseUrl;
  }

  // For high DPR screens, request higher resolution
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('dpr', dpr.toString());

  return url.toString();
};

/**
 * Generate responsive image srcset
 */
export const generateSrcSet = (
  baseUrl: string,
  sizes: number[] = [320, 640, 1280, 1920]
): string => {
  const url = new URL(baseUrl, window.location.origin);

  return sizes
    .map(size => {
      url.searchParams.set('w', size.toString());
      return `${url.toString()} ${size}w`;
    })
    .join(', ');
};

/**
 * Lazy load component with IntersectionObserver
 */
export const createLazyComponent = <P extends object>(
  componentLoader: () => Promise<{ default: React.ComponentType<P> }>,
  rootMargin: string = '50px'
) => {
  let Component: React.ComponentType<P> | null = null;
  let promise: Promise<void> | null = null;

  return React.forwardRef<any, P>((props, ref) => {
    const [isLoaded, setIsLoaded] = React.useState(false);

    React.useEffect(() => {
      if (!promise) {
        promise = componentLoader().then(module => {
          Component = module.default;
          setIsLoaded(true);
        });
      }

      return () => {
        // Cleanup
        promise = null;
        Component = null;
        setIsLoaded(false);
      };
    }, []);

    // Use IntersectionObserver to trigger loading
    const elementRef = React.useRef<any>(null);

    React.useEffect(() => {
      if (!isLoaded && elementRef.current && typeof IntersectionObserver !== 'undefined') {
        const observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              // Trigger loading
              promise?.then(() => {
                setIsLoaded(true);
              });
              observer.disconnect();
            }
          },
          { rootMargin }
        );

        observer.observe(elementRef.current);

        return () => {
          observer.disconnect();
        };
      }
    }, [isLoaded, rootMargin]);

    // Load immediately if no IntersectionObserver support
    React.useEffect(() => {
      if (!(typeof IntersectionObserver !== 'undefined') && !isLoaded && !promise) {
        promise = componentLoader().then(() => {
          setIsLoaded(true);
        });
      }
    }, [isLoaded, componentLoader]);

    if (!isLoaded) {
      return React.createElement('div', { ref: elementRef, style: { minHeight: '200px' } });
    }

    return React.createElement(Component as React.ElementType, { ...props, ref });
  });
};

/**
 * Optimize font loading
 */
export const optimizeFontLoading = () => {
  if ('fonts' in document) {
    const fontDisplay = 'swap';

    // Use font-display: swap for faster rendering
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-display: ${fontDisplay};
      }
    `;
    document.head.appendChild(style);
  }
};

/**
 * Detect network connection type
 */
export const getNetworkType = (): string => {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

  if (connection) {
    return connection.effectiveType || 'unknown';
  }

  return 'unknown';
};

/**
 * Check if user is on slow connection
 */
export const isSlowConnection = (): boolean => {
  const connection = (navigator as any).connection;

  if (connection) {
    // Consider slow if:
    // - effectiveType is 2g or slower
    // - saveData is true
    // - downlink is less than 1.5 Mbps
    return (
      connection.effectiveType === '2g' ||
      connection.effectiveType === 'slow-2g' ||
      connection.saveData === true ||
      (connection.downlink && connection.downlink < 1.5)
    );
  }

  return false;
};

/**
 * Debounce function for performance
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

/**
 * Throttle function for performance
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;

      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

/**
 * Request animation frame helper for smooth animations
 */
export const rafThrottle = <T extends (...args: any[]) => any>(
  func: T
): ((...args: Parameters<T>) => void) => {
  let ticking = false;

  return (...args: Parameters<T>) => {
    if (!ticking) {
      requestAnimationFrame(() => {
        func(...args);
        ticking = false;
      });
      ticking = true;
    }
  };
};

/**
 * Memoize expensive calculations
 */
export const memoize = <T extends (...args: any[]) => any>(
  func: T,
  keyGenerator?: (...args: Parameters<T>) => string
): ((...args: Parameters<T>) => ReturnType<T>) => {
  const cache = new Map<string, ReturnType<T>>();

  return (...args: Parameters<T>): ReturnType<T> => {
    const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args);

    if (cache.has(key)) {
      return cache.get(key)!;
    }

    const result = func(...args);
    cache.set(key, result);
    return result;
  };
};

/**
 * Virtual scroll helper for large lists
 */
export const useVirtualScroll = <T,>(
  items: T[],
  itemHeight: number,
  containerHeight: number,
  _renderItem: (item: T, index: number) => React.ReactNode
) => {
  const [scrollTop, setScrollTop] = React.useState(0);

  const visibleStart = Math.floor(scrollTop / itemHeight);
  const visibleEnd = Math.min(
    visibleStart + Math.ceil(containerHeight / itemHeight),
    items.length
  );

  const visibleItems = items.slice(visibleStart, visibleEnd);

  const totalHeight = items.length * itemHeight;
  const offsetY = visibleStart * itemHeight;

  return {
    visibleItems,
    totalHeight,
    offsetY,
    scrollTop,
    setScrollTop,
  };
};

/**
 * Detect if device is mobile
 */
export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Detect if device supports touch
 */
export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Get device memory info (Chrome only)
 */
export const getMemoryInfo = () => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    return {
      jsHeapSizeLimit: memory.jsHeapSizeLimit / 1048576, // Convert to MB
      totalJSHeapSize: memory.totalJSHeapSize / 1048576,
      usedJSHeapSize: memory.usedJSHeapSize / 1048576,
    };
  }
  return null;
};

/**
 * Monitor performance metrics
 */
export const getPerformanceMetrics = () => {
  if (!('performance' in window)) {
    return null;
  }

  const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

  return {
    // Page load timing
    domContentLoaded: perfData.domContentLoadedEventEnd - perfData.fetchStart,
    loadComplete: perfData.loadEventEnd - perfData.fetchStart,
    firstPaint: perfData.responseStart - perfData.fetchStart,

    // Resource timing
    resourceCount: performance.getEntriesByType('resource').length,
  };
};

/**
 * Log performance metrics (development only)
 */
export const logPerformanceMetrics = () => {
  if (process.env.NODE_ENV === 'development') {
    const metrics = getPerformanceMetrics();
    if (metrics) {
      console.log('Performance Metrics:', metrics);
    }

    const memory = getMemoryInfo();
    if (memory) {
      console.log('Memory Usage:', memory);
    }
  }
};

/**
 * Service Worker registration for offline support
 */
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register(
        '/sw.js',
        { scope: '/' }
      );
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
};

/**
 * Unregister service worker
 */
export const unregisterServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
  }
};

/**
 * Preload critical assets
 */
export const preloadCriticalAssets = () => {
  const criticalAssets = [
    '/fonts/main-font.woff2',
    '/static/resources/ai-vision.png',
    '/static/resources/ai-voice.png',
    '/static/resources/ai-digit.png',
  ];

  // Only preload if supported
  if ('fonts' in document) {
    preloadResources(criticalAssets);
  }
};

/**
 * Setup performance observers
 */
export const setupPerformanceObserver = () => {
  if ('PerformanceObserver' in window) {
    // Observe long tasks
    const longTaskObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        console.warn('Long task detected:', entry);
      }
    });
    longTaskObserver.observe({ entryTypes: ['measure'] });

    // Observe layout shifts
    const layoutShiftObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          console.warn('Layout shift detected:', entry);
        }
      }
    });
    layoutShiftObserver.observe({ entryTypes: ['layout-shift'] });

    return {
      longTaskObserver,
      layoutShiftObserver,
    };
  }
  return null;
};
